type NormalizeOptions = {
  /** Convert empty strings ("", "   ") to null */
  emptyStringToNull?: boolean;

  /** Remove keys with value === undefined */
  removeUndefined?: boolean;

  /** Remove empty objects {} */
  removeEmptyObjects?: boolean;

  /** Remove empty arrays [] */
  removeEmptyArrays?: boolean;
};

const DEFAULT_OPTIONS: Required<NormalizeOptions> = {
  emptyStringToNull: true,
  removeUndefined: true,
  removeEmptyObjects: false,
  removeEmptyArrays: false,
};

export function normalizeDeep<T>(value: T, opts?: NormalizeOptions): T {
  const options = { ...DEFAULT_OPTIONS, ...opts };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const walk = (v: any): any => {
    // primitives
    if (v == null) return v; // null or undefined

    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (options.emptyStringToNull && trimmed === '') return null;
      return v;
    }

    if (typeof v !== 'object') {
      return v;
    }

    // Date (or other special objects)
    if (v instanceof Date) {
      return v;
    }

    // Array
    if (Array.isArray(v)) {
      const arr = v
        .map(walk)
        .filter((x) => !(options.removeUndefined && x === undefined))
        .filter((x) => !(options.removeEmptyObjects && isEmptyObject(x)))
        .filter((x) => !(options.removeEmptyArrays && Array.isArray(x) && x.length === 0));

      if (options.removeEmptyArrays && arr.length === 0) return undefined;
      return arr;
    }

    // Object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: Record<string, any> = {};

    for (const [k, val] of Object.entries(v)) {
      const next = walk(val);

      if (options.removeUndefined && next === undefined) continue;
      if (options.removeEmptyObjects && isEmptyObject(next)) continue;
      if (options.removeEmptyArrays && Array.isArray(next) && next.length === 0) continue;

      out[k] = next;
    }

    if (options.removeEmptyObjects && Object.keys(out).length === 0) {
      return undefined;
    }

    return out;
  };

  return walk(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isEmptyObject(v: any): boolean {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0;
}
